import * as React from "react";
import {
    Alert,
    Button,
    ButtonGroup,
    Input,
    UncontrolledAccordion,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Badge,
    Table,
} from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFolderClosed,
    faFolderOpen,
    faLocationDot,
    faRoad,
    faDrawPolygon,
} from '@fortawesome/free-solid-svg-icons';
import * as Turf from "@turf/turf";
import ErrorBoundary from "app/ui/error-boundary";

import {
    formatLatLonPlain,
    formatLatLonDMS,
    formatLatLonDM,
    formatArea,
    formatDistance,
} from "app/lib/formatting";

// import { useSettings } from "../lib/settings";
// import { formatDate } from "app/lib/formatting";
// import { useSelector } from "react-redux";


export default function UploadForm() {

    const [importedData, setImportedData] = React.useState(null);

    const handleFiles = files => {

        if (files.length < 1) {
            return;
        }

        if (files.length > 1) {
            // This should never happen!
            console.warn("Received multiple files, only using the first one", files);
        }

        let file = files[0];
        let reader = new FileReader();
        reader.onload = event => {
            let text = event.target.result;
            let json = JSON.parse(text);
            setImportedData(json);
        };
        reader.readAsText(file);
    };

    if (importedData) {
        return (
            <DataDisplay
                data={importedData}
                onCancel={() => setImportedData(null)}
            />
        );
    }

    return (
        <>
            <div className="mb-3">
                <Input type="file" onChange={evt => handleFiles(evt.target.files)} />
            </div>
            <FileDropArea onFilesDropped={files => handleFiles(files)} />
        </>
    );
}


function FileDropArea({ onFilesDropped }) {

    const [isActive, setActive] = React.useState(false);

    const onDragOver = event => {
        event.preventDefault();
    };

    const onDragEnter = event => {
        setActive(true);
    };

    const onDragLeave = event => {
        setActive(false);
    };

    const onDragEnd = event => {
        setActive(false);
    };

    const onDrop = event => {
        event.stopPropagation();
        event.preventDefault();
        const files = event.dataTransfer.files;
        onFilesDropped(files);
    }

    let props = { onDragEnter, onDragLeave, onDragEnd, onDragOver, onDrop };
    let className = [
        "border border-3 rounded text-center",
        "d-flex flex-column justify-content-center align-items-center",
        isActive ? "border-primary" : "border-secondary",
    ];

    return (
        <div
            style={{ minHeight: 200 }}
            className={className.join(" ")}
            {...props}
        >
            <span>Drop file here</span>
        </div>
    );
}


function DataDisplay({ data }) {
    return (
        <div style={{ maxHeight: "80vh", overflow: "auto" }}>
            <GeoJSONDisplay data={data} />
            {false && <pre>
                {JSON.stringify(data, null, 4)}
            </pre>}
        </div>
    );
}


function GeoJSONDisplay({ data }) {
    return (
        <Accordion open="root" toggle={() => {}}>
            <GeoJSONObjectDisplay id="root" data={data} isOpen />
        </Accordion>
    );
}


function GeoJSONObjectDisplay(props) {
    const { data } = props;
    if (data.type === "FeatureCollection") {
        return <FeatureCollectionDisplay {...props} />;
    }
    if (data.type === "Feature") {
        return <FeatureDisplay {...props} />;
    }
    return <div>Invalid GeoJSON data</div>;
}


function FeatureCollectionDisplay({ id, data, isOpen }) {
    const [openItems, setOpenItems] = React.useState({});
    const toggleItem = (index) =>
        setOpenItems(state => ({
            ...state,
            [index]: !state[index],
        }));
    const openKeys =
        Object.entries(openItems)
              .filter(([key, val]) => !!val)
              .map(([key]) => key);

    // console.log("Open items", openItems, openKeys);

    return (
        <AccordionItem>

            <AccordionHeader targetId={id}>
                <span className="d-inline-block me-1">
                    <FontAwesomeIcon icon={isOpen ? faFolderOpen : faFolderClosed} />
                </span>
                {" "}Feature collection <Badge>{data.features.length}</Badge>
            </AccordionHeader>

            <AccordionBody accordionId={id}>
                <Accordion open={openKeys} toggle={toggleItem} stayOpen>
                    {data.features.map((feature, key) => (
                        <GeoJSONObjectDisplay
                            key={key}
                            id={key.toString()}
                            data={feature}
                            isOpen={openItems[key] || false}
                        />
                    ))}
                </Accordion>
            </AccordionBody>

        </AccordionItem>
    );
}


function FeatureDisplay({ id, data, isOpen }) {

    const renderTitle = () => {
        if (data.properties.name) {
            return (
                <span>
                    {data.properties.name}{" "}
                    <span className="text-muted">
                        ({data.geometry.type})
                    </span>
                </span>
            );
        }
        return <span className="text-muted">
            {data.geometry?.type}
        </span>
    };

    return (
        <AccordionItem>
            <AccordionHeader targetId={id}>
                <span className="d-inline-block me-1">
                    <FontAwesomeIcon icon={getFeatureTypeIcon(data.geometry?.type)} />
                </span>
                <span>{renderTitle()}</span>
            </AccordionHeader>
            <AccordionBody accordionId={id}>
                {isOpen && (<>
                    <PropertiesDisplay data={data.properties} />
                    <ErrorBoundary>
                        <GeometryDisplay feature={data} data={data.geometry} />
                    </ErrorBoundary>
                </>)}
            </AccordionBody>
        </AccordionItem>
    );
}


const FEATURE_TYPE_ICONS = {
    "Point": faLocationDot,
    "MultiPoint": faLocationDot,
    "LineString": faRoad,
    "MultiLineString": faRoad,
    "Polygon": faDrawPolygon,
    "MultiPolygon": faDrawPolygon,
};


const getFeatureTypeIcon = (featureType) =>
    FEATURE_TYPE_ICONS[featureType] || FEATURE_TYPE_ICONS["Point"];


function PropertiesDisplay({ data }) {
    const keyvals = Object.entries(data);
    if (!keyvals.length) {
        return (
            <div className="text-muted my-3">
                This feature has no properties
            </div>
        );
    }

    return (
        <Table className="my-3" striped dark hover bordered>
            <tbody>
                {keyvals.map(([key, value]) => (
                    <tr key={key}>
                        <th>{key}</th>
                        <td>{value}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}


function GeometryDisplay({ feature, data }) {
    if (data.type === "Point") {
        const [lon, lat] = data.coordinates;
        return (
            <div className="my-3">
                Point: {formatLatLonPlain([lat, lon])}
            </div>
        );
    }

    if (data.type === "MultiPoint") {
        const [lon, lat] = data.coordinates[0];
        return (
            <div className="my-3">
                Points: ({data.coordinates.length})
                First: {formatLatLonPlain([lat, lon])}
            </div>
        );
    }

    if (data.type === "LineString") {
        const [lonS, latS] = data.coordinates[0];
        const [lonE, latE] = data.coordinates[data.coordinates.length - 1];
        const length = Turf.length(feature, { units: "meters" });
        return (
            <div className="my-3">
                Length: {formatDistance(length)}{" "}
                From: {formatLatLonPlain([latS, lonS])}{" "}
                To: {formatLatLonPlain([latE, lonE])}.
            </div>
        );
    }

    if (data.type === "Polygon") {
        const center = Turf.center(feature);
        const [lon, lat] = center?.geometry?.coordinates || [0, 0];
        return (
            <div className="my-3">
                Area: {formatArea(Turf.area(feature))}{" "}
                Perimeter: {formatDistance(Turf.length(feature, { units: "meters" }))}{" "}
                Center: {formatLatLonPlain([lat, lon])}
            </div>
        );
    }

    return (
        <div className="my-3">
            <pre>{JSON.stringify(data, null, 4)}</pre>
        </div>
    );
}
